import React from 'react';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { FolderOpenIcon } from './FolderOpenIcon';
import { EditItemButton } from './EditItemButton';
import {
  abaHasContent,
  splitEmColunas,
  toPublicacoesAbas,
  type PublicacaoLinkCard,
  type PublicacoesData,
} from './helpers';
import { TITULO_FAIXA } from './typography';

interface PublicacoesEditaisSectionProps {
  data?: PublicacoesData;
  isEditMode?: boolean;
  /** Presente só no editor: `(slot da aba, índice do link)`. */
  onEditItem?: (aba: number, index: number) => void;
}

export const PUBLICACOES_TITLE = 'Publicações e Editais';

/* Cópia da mecânica de abas do `OndeAtuaSection` (mesmo padrão de
   `role=tablist/tab/tabpanel`, roving `tabIndex`, setas circulares). Extrair
   um `useTablist` compartilhado obrigaria mexer numa faixa já validada em
   produção — o custo de manter os dois trechos em paralelo é menor que o
   risco de regressão ali. */

/* `border` já na base, e a aba ativa pinta a borda da mesma cor do fundo: sem
   isso as duas pílulas ficariam com 2px de diferença de altura. */
const TAB_BASE =
  'shrink-0 snap-start cursor-pointer whitespace-nowrap rounded-full border px-5 py-2 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
const TAB_ACTIVE =
  'border-indigo-800 bg-indigo-800 text-purple-50 dark:border-[color:var(--mpf-dark-tab-purple-border)] dark:bg-[color:var(--mpf-dark-tag-purple-bg)] dark:text-[color:var(--mpf-dark-tag-purple-text)]';
/* Cores cheias, e não `bg-indigo-800/10`: o Tailwind não aplica modificador
   de opacidade a cor declarada como `var(--…)` pura — a classe simplesmente
   não é gerada, e a pílula ficaria com o cinza padrão que o navegador dá a
   `<button>` (o preflight do Tailwind está desligado aqui). */
const TAB_IDLE =
  'border-indigo-300 bg-indigo-100 text-indigo-900 hover:bg-indigo-200 dark:border-night-500 dark:bg-night-700 dark:text-night-100 dark:hover:bg-night-600 dark:hover:text-night-50';

/**
 * Faixa "Publicações e Editais" (bloco `mpfPublicacoesEditais`).
 *
 * Estrutura de `OndeAtuaSection` (cabeçalho fixo, abas acessíveis, régua),
 * mas sem corpo, CTA nem carrossel: o painel de cada aba é só uma lista de
 * links em duas colunas, com régua vertical no meio. No site só aparecem as
 * abas com conteúdo; no editor as quatro ficam visíveis.
 */
export const PublicacoesEditaisSection: React.FC<
  PublicacoesEditaisSectionProps
> = ({ data, isEditMode, onEditItem }) => {
  const abas = React.useMemo(() => {
    const all = toPublicacoesAbas(data);
    return isEditMode ? all : all.filter(abaHasContent);
  }, [data, isEditMode]);

  const [activeSlot, setActiveSlot] = React.useState(abas[0]?.slot ?? 1);
  const tabsRef = React.useRef<HTMLDivElement>(null);

  // A aba escolhida pode sumir enquanto se edita (esvaziada, por exemplo);
  // nesse caso a faixa volta para a primeira que sobrou.
  const active = abas.find((aba) => aba.slot === activeSlot) ?? abas[0] ?? null;

  if (!active) return null;

  /** Setas ←/→ andam entre as abas, como manda o padrão de `tablist`. */
  const handleTabKeyDown = (event: React.KeyboardEvent) => {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const current = abas.findIndex((aba) => aba.slot === active.slot);
    const next = abas[(current + step + abas.length) % abas.length];
    setActiveSlot(next.slot);
    const el = tabsRef.current?.querySelector<HTMLButtonElement>(
      `#publicacoes-aba-${next.slot}`,
    );
    // `preventScroll` + `scrollIntoView` escopado ao próprio botão: sem isso
    // o foco nativo rolaria a página inteira até o elemento, e não só a
    // fila de abas até a próxima pílula. `scrollIntoView` é opcional na
    // chamada (e não só no acesso) porque o jsdom, usado nos testes, não o
    // implementa — a navegação por teclado não pode quebrar por causa disso.
    el?.focus({ preventScroll: true });
    el?.scrollIntoView?.({
      inline: 'nearest',
      block: 'nearest',
      behavior: 'smooth',
    });
  };

  const { esquerda, direita } = splitEmColunas(active.links);

  return (
    /* Mesma moldura das outras faixas: traço, raio 16 e padding 16, com os
       16 entre cabeçalho e painel saindo do `gap`.
       `not-prose` porque a seção também é usada como bloco numa página comum,
       onde o `prose` do corpo do conteúdo vazaria para dentro — a explicação
       inteira está em `AcompanheRedesSection`. */
    <section
      aria-labelledby="capa-publicacoes-titulo"
      className="not-prose flex flex-col gap-4 rounded-2xl border border-gray-200 bg-surface-container-lowest p-4 dark:border-night-850 dark:bg-night-800"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <FolderOpenIcon className="h-6 w-6 shrink-0 text-indigo-500 dark:text-white" />
          {/* Título e ícone são fixos — não há campo editável para eles. */}
          <h2
            id="capa-publicacoes-titulo"
            className={`${TITULO_FAIXA} text-blue-800 dark:text-white`}
          >
            {PUBLICACOES_TITLE}
          </h2>
        </div>

        {abas.length > 1 && (
          /* `overflow-x-auto` sozinho corta o anel de foco (`ring-2
             ring-offset-2` soma 4px) porque implica `overflow-y: auto`; o
             `p-1` reserva o espaço e a `-m-1` devolve o alinhamento com o
             restante do cabeçalho. `snap-proximity`, e não `mandatory`, para
             o gesto de rolagem não ser sequestrado no meio do caminho. */
          <div
            ref={tabsRef}
            role="tablist"
            aria-label="Abas de publicações e editais"
            className="mpf-publicacoes__abas flex min-w-0 max-w-full flex-nowrap items-center gap-2 snap-x snap-proximity overflow-x-auto p-1 -m-1 w-full sm:w-auto"
          >
            {abas.map((aba) => {
              const selected = aba.slot === active.slot;
              return (
                <button
                  key={aba.slot}
                  type="button"
                  role="tab"
                  id={`publicacoes-aba-${aba.slot}`}
                  aria-selected={selected}
                  aria-controls={`publicacoes-painel-${aba.slot}`}
                  // Só a aba ativa fica na ordem de tabulação: o Tab entra e
                  // sai do grupo de abas, e as setas andam entre elas.
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActiveSlot(aba.slot)}
                  onKeyDown={handleTabKeyDown}
                  className={`${TAB_BASE} ${selected ? TAB_ACTIVE : TAB_IDLE}`}
                >
                  {aba.label || `Aba ${aba.slot}`}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Régua do design: 2px em `--indigo-500`, na largura do card.
          Decorativa, não separa conteúdo semântico. */}
      <div
        aria-hidden="true"
        className="h-0.5 w-full shrink-0 bg-indigo-500 dark:!bg-night-750"
      />

      {/* O `key` remonta o painel a cada troca de aba — é o que faz a
          animação de entrada (`_capa-publicacoes-editais.scss`) tocar de
          novo. Sem `columns-2`: o CSS multi-coluna balancearia as duas metades
          sozinho, mas não há como suprimir o separador do último item de cada
          coluna — por isso o split em duas `<ul>` feito em JS
          (`splitEmColunas`). */}
      <div
        key={active.slot}
        role="tabpanel"
        id={`publicacoes-painel-${active.slot}`}
        aria-labelledby={`publicacoes-aba-${active.slot}`}
        className="mpf-publicacoes__painel grid grid-cols-1 sm:grid-cols-2"
      >
        {/* `h-full` + `flex-col` na lista e `flex-1` em cada linha: as duas
            colunas são itens de grid em `stretch`, então a mais alta (a que
            tem um título quebrando em duas linhas) manda na altura. Sem isso a
            outra coluna terminaria antes e sobraria um vazio no pé; assim as
            linhas repartem a altura em partes iguais e o título fica centrado
            na sua faixa — mesmo arranjo do `AcoesRapidasCard`. */}
        <ul className="flex h-full min-w-0 flex-col divide-y divide-gray-200 sm:pr-6 dark:divide-night-850">
          {esquerda.map((link) => (
            <PublicacaoLinkRow
              key={link.key}
              link={link}
              isEditMode={isEditMode}
              onEdit={
                onEditItem
                  ? () => onEditItem(active.slot, link.sourceIndex)
                  : undefined
              }
            />
          ))}
        </ul>

        {/* Só existe com 2+ links, para não renderizar uma lista vazia. As
            colunas não têm régua entre elas: separa só o vão do `pr-6`/`pl-6`,
            48px no total. Todo `sm:` some no mobile, onde as duas viram uma
            coluna só — e aí o `border-t sm:border-t-0` costura a emenda entre
            o último item da esquerda e o primeiro da direita. */}
        {direita.length > 0 && (
          <ul className="flex h-full min-w-0 flex-col divide-y divide-gray-200 border-t border-gray-200 sm:border-t-0 sm:pl-6 dark:divide-night-850 dark:border-night-850">
            {direita.map((link) => (
              <PublicacaoLinkRow
                key={link.key}
                link={link}
                isEditMode={isEditMode}
                onEdit={
                  onEditItem
                    ? () => onEditItem(active.slot, link.sourceIndex)
                    : undefined
                }
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/** Uma linha do painel: link + lápis (só no editor), com `divide-y` cuidando
    do separador entre itens (nunca sobra um depois do último). */
const PublicacaoLinkRow: React.FC<{
  link: PublicacaoLinkCard;
  isEditMode?: boolean;
  onEdit?: () => void;
}> = ({ link, isEditMode, onEdit }) => (
  /* O fundo do hover mora aqui, e não no `<a>`: assim o realce cobre a linha
     inteira, de borda a borda da coluna, incluindo o padding lateral e o
     espaço do lápis do editor. `focus-within` porque quem recebe o foco de
     teclado é o `<a>` lá dentro — sem ele a navegação por Tab não pintaria a
     faixa. O `group` é o que deixa a seta reagir ao hover da linha toda.
     O `<li>` continua `items-center` só para o lápis do editor (tamanho fixo
     `h-8`, ver `EditItemButton`) — um item com altura fixa não estica com
     `items-stretch`, então ele precisa do `center` para ficar no meio. */
  <li className="group flex flex-1 items-center gap-2 rounded-lg px-4 transition-colors hover:bg-brand-50 hover:ring-1 hover:ring-inset hover:ring-brand-200 focus-within:bg-brand-50 focus-within:ring-1 focus-within:ring-inset focus-within:ring-brand-200 dark:hover:bg-night-700 dark:hover:ring-0 dark:focus-within:bg-night-700 dark:focus-within:ring-0">
    <MPFLink
      href={link.href}
      openInNewTab={link.external || undefined}
      // `min-w-0` + `break-words`: sem eles um título longo estoura a coluna
      // em vez de quebrar linha. `self-stretch` tira o link do `items-center`
      // do `<li>` e o estica pela altura INTEIRA da linha — que cresce além
      // do próprio conteúdo quando a coluna vizinha tem mais links (o
      // `flex-1`/`h-full` da `<ul>` acima). Um `<a>` só `block` fica do
      // tamanho do próprio texto, e é essa caixa pequena que o `items-center`
      // do `<li>` centralizava — sobrava espaço em branco visível embaixo
      // dela. Com `flex items-center self-stretch`, o `<a>` passa a ocupar a
      // linha inteira e centraliza o título dentro dela mesma, mesma ideia
      // do link do `AcoesRapidasCard`. O `my-4` é o `--spacing-lg` (16px) da
      // escala do projeto: o `self-stretch` desconta as margens antes de
      // esticar, então a folga em cima e embaixo é preservada.
      //
      // Sem seta e sem `hover:underline`: o item é o botão `ghost` tamanho L
      // do Design System (3086:103770, AB#11506) — texto `primary` em repouso
      // e, no hover/foco, a caixa `brand-50` com contorno de 1px `brand-200`
      // e raio 8 pintada pelo `<li>`, com o texto em peso medium e na mesma
      // cor (`group-hover:text-primary` segura o `a:hover` global, que o
      // clarearia para `blue-500`). A seta
      // deslizante que havia aqui era do `AcoesRapidasCard`, não deste bloco.
      className="my-4 flex min-w-0 flex-1 items-center self-stretch break-words rounded text-base font-normal text-primary transition-colors group-hover:font-medium group-hover:text-primary group-focus-within:font-medium group-focus-within:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-night-100 dark:group-hover:text-white dark:group-focus-within:text-white"
    >
      {link.title}
    </MPFLink>

    {isEditMode && onEdit && (
      <EditItemButton
        label={`Editar link: ${link.title || 'sem título'}`}
        onClick={onEdit}
      />
    )}
  </li>
);

export default PublicacoesEditaisSection;
